import React, { useState, useEffect, useRef, useCallback } from 'react';
import './tmi.css';
import './ClientQueries.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridSortChangeEvent,
    GridItemChangeEvent,
    GridCellProps,
    GridColumn,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import ProjectSelectionDialog from './ProjectSelectionDialog';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type ClientQueriesProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: ClientQueriesState;
    onSaveState?: (menuItemId: number, state: ClientQueriesState) => void;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface ClientQueryRow {
    clientQueryID: number;
    description: string | null;
    sql: string | null;
    isExcelViewable: boolean;
    actionType: string | null;
    validation: string | null;
    clientQueryValidationTypeID: number;
    inActive: boolean;
    preValidation: string | null;
    helpDescription: string | null;
    c4PCExpiredMessage: string | null;
    c4PCDisabledMessage: string | null;
    validationHelpDescription: string | null;
    lastChangedDeviceID: number | null;
    lastChangedBy: string | null;
    lastChangedOn: string | null;
    clientQueryCode: string | null;
    clientQueryGrpId: number | null;
    parameters?: ClientQueryParameter[];
}

interface ClientQueryParameter {
    clientQueryParameterID: number;
    clientQueryID: number;
    name: string | null;
    displayName: string | null;
    attributeClassID: number;
    required: boolean;
    visible: boolean;
    useLookupValue: boolean;
    sortOrder: number;
    helpDescription: string | null;
    lookupSql: string | null;
    validation: string | null;
    defaultValueSql: string | null;
    inEdit?: boolean;
}



interface ClientQueriesState {
    data: ClientQueryRow[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filterTexts: { [key: string]: string };
    headerFocusField?: string | null;
    editItemId: number | null;
    originalItem: ClientQueryRow | null;
    selectedIds: Set<number>;
    menuDataItem: ClientQueryRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    actionTypesDropdown: LookupListItem[];
    validationTypesDropdown: LookupListItem[];
    clientQueryGroupsDropdown: LookupListItem[];
    attributeClassesDropdown: LookupListItem[];
    newClientQueryDlgOpen: boolean;
    newClientQueryDlgSaving: boolean;
    newClientQueryDlgError: string;
    newClientQueryDlgDescription: string;
    newClientQueryDlgSQL: string;
    newClientQueryDlgActionType: string;
    newClientQueryDlgClientQueryCode: string;
    newClientQueryDlgIsExcelViewable: boolean;
    newClientQueryDlgIsActive: boolean;
    newClientQueryDlgClientQueryValidationTypeID: string;
    newClientQueryDlgClientQueryGrpId: string;
    deleteConfirmItem: ClientQueryRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    scriptEditItem: ClientQueryRow | null;
    scriptEditPreValidation: string;
    scriptEditValidation: string;
    scriptEditSQL: string;
    scriptEditSaving: boolean;
    scriptEditError: string;
    scriptEditTabIndex: number;
    parametersItem: ClientQueryRow | null;
    parametersList: ClientQueryParameter[];
    parametersLoading: boolean;
    parametersError: string;
    parameterDetailsDialogOpen: boolean;
    parameterDetailsItem: ClientQueryParameter | null;
    parameterDetailsLookupSql: string;
    parameterDetailsValidation: string;
    parameterDetailsHelpDescription: string;
    parameterDetailsDefaultValue: string;
    parameterDetailsSelectedTab: number;
    parameterDetailsEditingId: number | null;
    parameterDeleteConfirmItem: ClientQueryParameter | null;
    parameterDeleteInProgress: boolean;
    permissionsDialogOpen: boolean;
    permissionsItem: ClientQueryRow | null;
    permissionsSplPagesData: any[];
    permissionsUserRolesData: any[];
    permissionsPacklistTypesData: any[];
    permissionsItemTypesData: any[];
    permissionsSelectedTab: number;
    permissionsSplPagesSelected: number[];
    permissionsUserRolesSelected: number[];
    permissionsPacklistTypesSelected: number[];
    permissionsItemTypesSelected: number[];
    permissionsSplPagesSort: SortDescriptor[];
    permissionsUserRolesSort: SortDescriptor[];
    permissionsPacklistTypesSort: SortDescriptor[];
    permissionsItemTypesSort: SortDescriptor[];
    itemTypePacklistsData: any[];
    itemTypePacklistsSelected: number[];
    itemTypePacklistsSort: SortDescriptor[];
    itemTypePacklistsSelectedItemTypeValue: number | null;
    mainGridSelectedId: number | null;
    parametersGridSelectedId: number | null;
    splPagesGridSelectedValue: any;
    userRolesGridSelectedValue: any;
    packlistTypesGridSelectedValue: any;
    itemTypesGridSelectedValue: any;
    itemTypePacklistsGridSelectedValue: any;
    copyToNewDlgOpen: boolean;
    copyToNewSourceItem: ClientQueryRow | null;
    copyToNewDescription: string;
    copyToNewCode: string;
    copyToNewSaving: boolean;
    copyToNewError: string;
    importFromProjectDlgOpen: boolean;
    importAvailableProjects: LookupListItem[];
    importSelectedProject: LookupListItem | null;
    importQuerySelectDlgOpen: boolean;
    importAvailableQueries: LookupListItem[];
    importSelectedQuery: LookupListItem | null;
    importQueryLoading: boolean;
    importQueryError: string;
    importDetailsLoading: boolean;
    importDetailsError: string;
    importImportDescription: string;
    importImportCode: string;
    importScriptSQL: string;
    importScriptValidation: string;
    importScriptPreValidation: string;
    importScriptTabIndex: number;
    importImporting: boolean;
}

const ActionTypeLookupList: LookupListItem[] = [
    { value: 'data_grid', display: 'Data Grid' },
    { value: 'ssrs_report', display: 'SSRS Report' },
    { value: 'export_to_excel', display: 'Export to Excel' },
];

const DATA_ITEM_KEY = 'clientQueryID';
const EDIT_FIELD = 'inEdit';
const SELECTED_FIELD = 'selected';

const ClientQueries: React.FC<ClientQueriesProps> = (props) => {
    const STORAGE_KEY = 'ClientQueries_state_v1';
    const MENU_FLIP_OFFSET = 8;
    const menuBlurTimeoutRef = useRef<any>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);




    // attempt to restore saved UI state from sessionStorage
    const savedRaw = (() => {
        try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const saved = savedRaw ? JSON.parse(savedRaw) : null;

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters) via stateToLoad/onSaveState, so
    // the page doesn't need to refilter/reselect. On first visit (or a full page
    // reload) there is no stateToLoad, so fall back to the sessionStorage-seeded defaults.
    const [state, setState] = useState<ClientQueriesState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: saved?.sort ?? [{ field: 'Description', dir: 'asc' }],
        filterTexts: {},
        headerFocusField: null,
        editItemId: null,
        originalItem: null,
        selectedIds: new Set(saved?.selectedIds ?? []),
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        actionTypesDropdown: ActionTypeLookupList,
        validationTypesDropdown: [],
        clientQueryGroupsDropdown: [],
        attributeClassesDropdown: [],
        newClientQueryDlgOpen: false,
        newClientQueryDlgSaving: false,
        newClientQueryDlgError: '',
        newClientQueryDlgDescription: '',
        newClientQueryDlgSQL: '',
        newClientQueryDlgActionType: '',
        newClientQueryDlgClientQueryCode: '',
        newClientQueryDlgIsExcelViewable: false,
        newClientQueryDlgIsActive: true,
        newClientQueryDlgClientQueryValidationTypeID: '',
        newClientQueryDlgClientQueryGrpId: '',
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        scriptEditItem: null,
        scriptEditPreValidation: '',
        scriptEditValidation: '',
        scriptEditSQL: '',
        scriptEditSaving: false,
        scriptEditError: '',
        scriptEditTabIndex: 0,
        parametersItem: null,
        parametersList: [],
        parametersLoading: false,
        parametersError: '',
        parameterDetailsDialogOpen: false,
        parameterDetailsItem: null,
        parameterDetailsLookupSql: '',
        parameterDetailsValidation: '',
        parameterDetailsHelpDescription: '',
        parameterDetailsDefaultValue: '',
        parameterDetailsSelectedTab: 0,
        parameterDetailsEditingId: null,
        parameterDeleteConfirmItem: null,
        parameterDeleteInProgress: false,
        permissionsDialogOpen: false,
        permissionsItem: null,
        permissionsSplPagesData: [],
        permissionsUserRolesData: [],
        permissionsPacklistTypesData: [],
        permissionsItemTypesData: [],
        permissionsSelectedTab: 0,
        permissionsSplPagesSelected: [],
        permissionsUserRolesSelected: [],
        permissionsPacklistTypesSelected: [],
        permissionsItemTypesSelected: [],
        permissionsSplPagesSort: [],
        permissionsUserRolesSort: [],
        permissionsPacklistTypesSort: [],
        permissionsItemTypesSort: [],
        itemTypePacklistsData: [],
        itemTypePacklistsSelected: [],
        itemTypePacklistsSort: [],
        itemTypePacklistsSelectedItemTypeValue: null,
        mainGridSelectedId: null,
        parametersGridSelectedId: null,
        splPagesGridSelectedValue: null,
        userRolesGridSelectedValue: null,
        packlistTypesGridSelectedValue: null,
        itemTypesGridSelectedValue: null,
        itemTypePacklistsGridSelectedValue: null,
        copyToNewDlgOpen: false,
        copyToNewSourceItem: null,
        copyToNewDescription: '',
        copyToNewCode: '',
        copyToNewSaving: false,
        copyToNewError: '',
        importFromProjectDlgOpen: false,
        importAvailableProjects: [],
        importSelectedProject: null,
        importQuerySelectDlgOpen: false,
        importAvailableQueries: [],
        importSelectedQuery: null,
        importQueryLoading: false,
        importQueryError: '',
        importDetailsLoading: false,
        importDetailsError: '',
        importImportDescription: '',
        importImportCode: '',
        importScriptSQL: '',
        importScriptValidation: '',
        importScriptPreValidation: '',
        importScriptTabIndex: 0,
        importImporting: false,
    });

    const saveState = useCallback(() => {
        try {
            const toSave = {
                sort: state.sort,
                selectedIds: Array.from(state.selectedIds ?? []),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [state.sort, state.selectedIds]);

    // Ref to the latest state (kept in sync on every render below) so the unmount
    // cleanup effect, which only runs once on mount/unmount, can report the
    // up-to-date state rather than a stale snapshot captured on the initial render.
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        fetchData();
        fetchValidationTypesDropdown();
        fetchClientQueryGroupsDropdown();
        fetchAttributeClassesDropdown();
        return () => {
            // cleanup on unmount
            saveState();
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
            if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close menu when clicking outside of it
    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                closeRowMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [state.menuDataItem]);

    const fetchData = () => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetClientQueries';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({ ...prev, data: res.results, loading: false }));
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        break;
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + err.toString(), loading: false }));
            });
    }

    const fetchValidationTypesDropdown = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetValidationTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, validationTypesDropdown: res.results }));
                }
            })
            .catch(err => {
                // Silently fail for dropdown data
                console.error('Error fetching validation types dropdown:', err);
            });
    }

    const fetchClientQueryGroupsDropdown = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetClientQueryGroupsForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, clientQueryGroupsDropdown: res.results }));
                }
            })
            .catch(err => {
                // Silently fail for dropdown data
                console.error('Error fetching client query groups dropdown:', err);
            });
    }

    const fetchAttributeClassesDropdown = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetAttributeClassesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, attributeClassesDropdown: res.results }));
                }
            })
            .catch(err => {
                // Silently fail for dropdown data
                console.error('Error fetching attribute classes dropdown:', err);
            });
    }

    const fetchSPLPagesForPermissions = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetSPLPagesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, permissionsSplPagesData: res.results }));
                }
            })
            .catch(err => {
                console.error('Error fetching SPL pages for permissions:', err);
            });
    }

    const fetchUserRolesForPermissions = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetUserRolesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, permissionsUserRolesData: res.results }));
                }
            })
            .catch(err => {
                console.error('Error fetching user roles for permissions:', err);
            });
    }

    const fetchPacklistTypesForPermissions = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, permissionsPacklistTypesData: res.results }));
                }
            })
            .catch(err => {
                console.error('Error fetching packlist types for permissions:', err);
            });
    }

    const fetchItemTypesForPermissions = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, permissionsItemTypesData: res.results }));
                }
            })
            .catch(err => {
                console.error('Error fetching item types for permissions:', err);
            });
    }

    const fetchPacklistTypesForItemTypePacklists = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, itemTypePacklistsData: res.results }));
                }
            })
            .catch(err => {
                console.error('Error fetching packlist types for item type packlists:', err);
            });
    }

    const fetchItemTypeClientQueryPacklistTypes = (itemTypeID: number, clientQueryID: number) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemTypeClientQueryPacklistTypesForDropDown?ItemTypeID=' + itemTypeID + '&ClientQueryID=' + clientQueryID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    const selectedIds = res.results.map((item: any) => typeof item === 'number' ? item : Number(item.value));
                    setState(prev => ({ ...prev, itemTypePacklistsSelected: selectedIds }));
                }
            })
            .catch(err => {
                console.error('Error fetching item type client query packlist types:', err);
            });
    }

    const fetchUserRoleSPLPages = (clientQueryID: number) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetSPLPageClientQueriesForDropDown?clientQueryID=' + clientQueryID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && res.result && Array.isArray(res.result.splPages)) {
                    const splPages = res.result.splPages;
                    // Extract security IDs for checkbox checking
                    const selectedIds = splPages.map((item: any) => item.securityID);
                    // Update grid data with multiSelect values using prev state
                    setState(prev => {
                        const updatedGridData = prev.permissionsSplPagesData.map(gridItem => {
                            const selectedItem = splPages.find((r: any) => Number(r.securityID) === Number(gridItem.value));
                            return selectedItem 
                                ? { ...gridItem, multiSelect: selectedItem.multiSelect === true }
                                : { ...gridItem, multiSelect: false };
                        });
                        return {
                            ...prev,
                            permissionsSplPagesSelected: selectedIds,
                            permissionsSplPagesData: updatedGridData
                        };
                    });
                }
            })
            .catch(err => {
                console.error('Error fetching selected SPL pages:', err);
            });
    }

    const fetchUserRoleClientQueries = (clientQueryID: number) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetUserRoleClientQueriesForDropDown?clientQueryID=' + clientQueryID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    // Extract IDs for checkbox checking - results may be numbers or objects with securityID
                    const selectedIds = res.results.map(item => typeof item === 'number' ? item : item.securityID);
                    setState(prev => ({ ...prev, permissionsUserRolesSelected: selectedIds }));
                }
            })
            .catch(err => {
                console.error('Error fetching selected user roles:', err);
            });
    }

    const fetchUserRolePacklistTypes = (clientQueryID: number) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypeClientQueriesForDropDown?clientQueryID=' + clientQueryID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    // Extract IDs for checkbox checking - results may be numbers or objects with securityID
                    const selectedIds = res.results.map(item => typeof item === 'number' ? item : item.securityID);
                    setState(prev => ({ ...prev, permissionsPacklistTypesSelected: selectedIds }));
                }
            })
            .catch(err => {
                console.error('Error fetching selected packlist types:', err);
            });
    }

    const fetchUserRoleItemTypes = (clientQueryID: number) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemTypeClientQueriesForDropDown?clientQueryID=' + clientQueryID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    // Extract IDs for checkbox checking - results may be numbers or objects with securityID
                    const selectedIds = res.results.map(item => typeof item === 'number' ? item : item.securityID);
                    setState(prev => ({ ...prev, permissionsItemTypesSelected: selectedIds }));
                }
            })
            .catch(err => {
                console.error('Error fetching selected item types:', err);
            });
    }

    const saveUserRoleClientQuery = (selectedUserRoles: number[]) => {
        if (!state.permissionsItem) return;
        
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateUserRoleClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                Checked: selectedUserRoles,
                Selected: state.permissionsItem.clientQueryID
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK') {
                    console.log('User roles saved successfully');
                }
            })
            .catch(err => {
                console.error('Error saving user roles:', err);
            });
    }

    const saveSplPageClientQuery = (selectedSplPages: number[]) => {
        if (!state.permissionsItem) return;

        const splPagesList = selectedSplPages.map(securityId => ({
            SecurityID: securityId,
            MultiSelect: state.permissionsSplPagesData.find(item => Number(item.value) === securityId)?.multiSelect ?? false
        }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateSPLPageClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                ClientQueryID: state.permissionsItem.clientQueryID,
                SPLPages: splPagesList
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK') {
                    console.log('SPL Pages saved successfully');
                } else {
                    console.error('Error saving SPL Pages:', res.callStatusMessage);
                }
            })
            .catch(err => {
                console.error('Error saving SPL Pages:', err);
            });
    }

    const handleSplPageMultiSelectChange = (dataItem: any, checked: boolean) => {
        const updatedData = state.permissionsSplPagesData.map(item =>
            item.value === dataItem.value
                ? { ...item, multiSelect: checked }
                : item
        );
        setState(prev => ({
            ...prev,
            permissionsSplPagesData: updatedData
        }));
        // Save with updated multiSelect flag
        const splPagesList = state.permissionsSplPagesSelected.map(securityId => ({
            SecurityID: securityId,
            MultiSelect: updatedData.find(item => Number(item.value) === securityId)?.multiSelect ?? false
        }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateSPLPageClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                ClientQueryID: state.permissionsItem!.clientQueryID,
                SPLPages: splPagesList
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    console.error('Error saving SPL Pages:', res.callStatusMessage);
                }
            })
            .catch(err => {
                console.error('Error saving SPL Pages:', err);
            });
    }

    const savePacklistTypesClientQuery = (selectedPacklistTypes: number[]) => {
        if (!state.permissionsItem) return;
        
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryPackListType';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                Checked: selectedPacklistTypes,
                Selected: state.permissionsItem.clientQueryID
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK') {
                    console.log('Packlist types saved successfully');
                }
            })
            .catch(err => {
                console.error('Error saving packlist types:', err);
            });
    }

    const saveItemTypesClientQuery = (selectedItemTypes: number[]) => {
        if (!state.permissionsItem) return;
        
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryItemType';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                Checked: selectedItemTypes,
                Selected: state.permissionsItem.clientQueryID
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK') {
                    console.log('Item types saved successfully');
                }
            })
            .catch(err => {
                console.error('Error saving item types:', err);
            });
    }

    const saveItemTypeClientQueryPackListType = (selectedPacklistTypes: number[]) => {
        if (!state.permissionsItem || state.itemTypePacklistsSelectedItemTypeValue === null) return;
        
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateItemTypeClientQueryPackListType';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                ItemTypeID: state.itemTypePacklistsSelectedItemTypeValue,
                ClientQueryID: state.permissionsItem.clientQueryID,
                SelectedPackListTypes: selectedPacklistTypes
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK') {
                    console.log('Item Type Packlist types saved successfully');
                }
            })
            .catch(err => {
                console.error('Error saving item type packlist types:', err);
            });
    }

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const currentSort = prev.sort && prev.sort.length > 0 && prev.sort[0].field === field ? prev.sort[0].dir : undefined;
            const newDir = currentSort === 'asc' ? 'desc' : currentSort === 'desc' ? undefined : 'asc';
            return {
                ...prev,
                sort: newDir ? [{ field, dir: newDir as 'asc' | 'desc' }] : []
            };
        });
    }, []);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setState(prev => ({
            ...prev,
            filterTexts: {
                ...prev.filterTexts,
                [field]: e.target.value
            }
        }));
    }, []);

    const handlePermissionsSplPagesSortChange = useCallback((e: GridSortChangeEvent) => {
        setState(prev => ({ ...prev, permissionsSplPagesSort: e.sort }));
    }, []);

    const handlePermissionsUserRolesSortChange = useCallback((e: GridSortChangeEvent) => {
        setState(prev => ({ ...prev, permissionsUserRolesSort: e.sort }));
    }, []);

    const handlePermissionsPacklistTypesSortChange = useCallback((e: GridSortChangeEvent) => {
        setState(prev => ({ ...prev, permissionsPacklistTypesSort: e.sort }));
    }, []);

    const handlePermissionsItemTypesSortChange = useCallback((e: GridSortChangeEvent) => {
        setState(prev => ({ ...prev, permissionsItemTypesSort: e.sort }));
    }, []);

    const handleItemTypePacklistsSortChange = (e: GridSortChangeEvent) => {
        setState(prev => ({ ...prev, itemTypePacklistsSort: e.sort }));
    }

    const handleMainGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, mainGridSelectedId: e.dataItem.clientQueryID }));
    }

    const handleParametersGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, parametersGridSelectedId: e.dataItem.clientQueryParameterID }));
    }

    const handleSplPagesGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, splPagesGridSelectedValue: e.dataItem.value }));
    }

    const handleUserRolesGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, userRolesGridSelectedValue: e.dataItem.value }));
    }

    const handlePacklistTypesGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, packlistTypesGridSelectedValue: e.dataItem.value }));
    }

    const handleItemTypePacklistsGridRowClick = (e: any) => {
        setState(prev => ({ ...prev, itemTypePacklistsGridSelectedValue: e.dataItem.value }));
    }




    const handleEnterEdit = (dataItem: ClientQueryRow) => {
        const original = state.data.find(d => d.clientQueryID === dataItem.clientQueryID);
        setState(prev => ({ ...prev, editItemId: dataItem.clientQueryID, originalItem: original ? { ...original } : null }));
    }

    const handleItemChange = (e: GridItemChangeEvent) => {
        const { field, value, dataItem } = e;
        if (!field || field === 'clientQueryID') return;
        setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === dataItem.clientQueryID ? { ...item, [field]: value } : item) }));
    }

    const handleSave = (dataItem: ClientQueryRow) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(dataItem),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error saving Query: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, editItemId: null, originalItem: null, errorMsg: '' }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error saving Query: ' + err.toString() }));
            });
    }

    const handleCancel = () => {
        const { originalItem } = state;
        setState(prev => ({ ...prev, data: originalItem ? prev.data.map(item => item.clientQueryID === originalItem.clientQueryID ? originalItem : item) : prev.data, editItemId: null, originalItem: null }));
    }


    const openDeleteConfirm = (dataItem: ClientQueryRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: dataItem, deleteErrorMsg: '' }));
    }

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
    }
    const confirmDelete = () => {
        const { deleteConfirmItem } = state;
        if (!deleteConfirmItem) return;
        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(deleteConfirmItem.clientQueryID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeDeleteConfirm();
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + res.callStatusMessage }));
                } else {
                    closeDeleteConfirm();
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + err.toString() }));
            });
    }



    const openRowMenu = (dataItem: ClientQueryRow, anchor: HTMLElement) => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        try {
            const ar = anchor.getBoundingClientRect();
            const anchorRect = { left: Math.round(ar.left), top: Math.round(ar.top), right: Math.round(ar.right), bottom: Math.round(ar.bottom), width: Math.round(ar.width), height: Math.round(ar.height) };
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } catch {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
    }

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null }));
    }

    const openScriptEditDialog = (item: ClientQueryRow) => {
        setState(prev => ({
            ...prev,
            scriptEditItem: item,
            scriptEditPreValidation: item.preValidation ?? '',
            scriptEditValidation: item.validation ?? '',
            scriptEditSQL: item.sql ?? '',
            scriptEditSaving: false,
            scriptEditError: '',
            parametersItem: item,
            parametersList: item.parameters || [],
            parametersLoading: false,
            parametersError: '',
            parametersGridSelectedId: null,
        }));
        closeRowMenu();
    }

    const closeScriptEditDialog = () => {
        setState(prev => ({
            ...prev,
            data: prev.data.map(item =>
                item.clientQueryID === prev.parametersItem?.clientQueryID
                    ? {
                        ...item,
                        parameters: prev.parametersList
                            .filter(parameter => parameter.clientQueryParameterID !== 0)
                            .map(parameter => ({ ...parameter, inEdit: false })),
                    }
                    : item
            ),
            scriptEditItem: null,
            scriptEditPreValidation: '',
            scriptEditValidation: '',
            scriptEditSQL: '',
            scriptEditSaving: false,
            scriptEditError: '',
            scriptEditTabIndex: 0,
            parametersItem: null,
            parametersList: [],
            parametersLoading: false,
            parametersError: '',
            parametersGridSelectedId: null,
        }));
    }

    const submitScriptEdit = () => {
        const { scriptEditItem, scriptEditPreValidation, scriptEditValidation, scriptEditSQL } = state;
        if (!scriptEditItem) return;
        setState(prev => ({ ...prev, scriptEditSaving: true, scriptEditError: '' }));

        const updatedItem = {
            ...scriptEditItem,
            preValidation: scriptEditPreValidation,
            validation: scriptEditValidation,
            sql: scriptEditSQL,
            parameters: state.parametersList
                .filter(parameter => parameter.clientQueryParameterID !== 0)
                .map(parameter => ({ ...parameter, inEdit: false })),
        };

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(updatedItem),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeScriptEditDialog();
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, scriptEditSaving: false, scriptEditError: 'Error saving scripts: ' + res.callStatusMessage }));
                } else {
                    closeScriptEditDialog();
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, scriptEditSaving: false, scriptEditError: 'Error saving scripts: ' + err.toString() }));
            });
    }

    const saveParameterRow = (dataItem: ClientQueryParameter, fields: {
        sortOrder: number;
        name: string;
        displayName: string;
        attributeClassID: number;
        required: boolean;
        visible: boolean;
        useLookupValue: boolean;
    }) => {
        const paramData = {
            ClientQueryParameterID: dataItem.clientQueryParameterID,
            ClientQueryID: dataItem.clientQueryID,
            Name: fields.name,
            DisplayName: fields.displayName,
            AttributeClassID: fields.attributeClassID,
            Required: fields.required,
            Visible: fields.visible,
            UseLookupValue: fields.useLookupValue,
            SortOrder: fields.sortOrder,
            HelpDescription: dataItem.helpDescription ?? '',
            LookupSql: dataItem.lookupSql ?? '',
            Validation: dataItem.validation ?? '',
            DefaultValueSql: dataItem.defaultValueSql ?? '',
        };

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryParameter';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(paramData),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    alert('Error saving parameter: ' + res.callStatusMessage);
                } else {
                    // For new parameters, adopt the ID returned by the server
                    const savedId = dataItem.clientQueryParameterID === 0
                        ? (res.result ?? dataItem.clientQueryParameterID)
                        : dataItem.clientQueryParameterID;
                    setState(prev => ({
                        ...prev,
                        parametersList: prev.parametersList.map(p =>
                            p.clientQueryParameterID === dataItem.clientQueryParameterID
                                ? {
                                    ...p,
                                    clientQueryParameterID: savedId,
                                    sortOrder: paramData.SortOrder,
                                    name: paramData.Name,
                                    displayName: paramData.DisplayName,
                                    attributeClassID: paramData.AttributeClassID,
                                    required: paramData.Required,
                                    visible: paramData.Visible,
                                    useLookupValue: paramData.UseLookupValue,
                                    inEdit: false
                                }
                                : p
                        )
                    }));
                }
            })
            .catch(err => {
                alert('Error saving parameter: ' + err.toString());
            });
    }

    const openPermissionsDialog = (item: ClientQueryRow) => {
        setState(prev => ({
            ...prev,
            permissionsDialogOpen: true,
            permissionsItem: item,
            permissionsSplPagesData: [],
            permissionsUserRolesData: [],
            permissionsPacklistTypesData: [],
            permissionsItemTypesData: [],
            permissionsSplPagesSelected: [],
            permissionsUserRolesSelected: [],
            permissionsPacklistTypesSelected: [],
            permissionsItemTypesSelected: [],
            itemTypePacklistsData: [],
            itemTypePacklistsSelected: [],
            itemTypePacklistsSelectedItemTypeValue: null,
        }));
        closeRowMenu();
        // Fetch permissions data
        fetchSPLPagesForPermissions();
        fetchUserRolesForPermissions();
        fetchPacklistTypesForPermissions();
        fetchItemTypesForPermissions();
        fetchPacklistTypesForItemTypePacklists();
        // Fetch selected items
        fetchUserRoleSPLPages(item.clientQueryID);
        fetchUserRoleClientQueries(item.clientQueryID);
        fetchUserRolePacklistTypes(item.clientQueryID);
        fetchUserRoleItemTypes(item.clientQueryID);
    }

    const closePermissionsDialog = () => {
        setState(prev => ({
            ...prev,
            permissionsDialogOpen: false,
            permissionsItem: null,
            permissionsSplPagesData: [],
            permissionsUserRolesData: [],
            permissionsPacklistTypesData: [],
            permissionsItemTypesData: [],
            permissionsSelectedTab: 0,
            permissionsSplPagesSelected: [],
            permissionsUserRolesSelected: [],
            permissionsPacklistTypesSelected: [],
            permissionsItemTypesSelected: [],
            itemTypePacklistsData: [],
            itemTypePacklistsSelected: [],
            itemTypePacklistsSelectedItemTypeValue: null,
        }));
    }

    const openCopyToNewDialog = (item: ClientQueryRow) => {
        setState(prev => ({
            ...prev,
            copyToNewDlgOpen: true,
            copyToNewSourceItem: item,
            copyToNewDescription: `${item.description} (Copy)`,
            copyToNewCode: `${item.clientQueryCode}_copy`,
            copyToNewSaving: false,
            copyToNewError: '',
        }));
        closeRowMenu();
    }

    const closeCopyToNewDialog = () => {
        setState(prev => ({
            ...prev,
            copyToNewDlgOpen: false,
            copyToNewSourceItem: null,
            copyToNewDescription: '',
            copyToNewCode: '',
            copyToNewSaving: false,
            copyToNewError: '',
        }));
    }

    const openImportFromProjectDialog = () => {
        setState(prev => ({
            ...prev,
            importFromProjectDlgOpen: true,
            importAvailableProjects: [],
            importSelectedProject: null,
            importImportDescription: '',
            importImportCode: ''
        }));

        // Fetch all projects
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetProjectsForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({
                        ...prev,
                        importAvailableProjects: res.results
                    }));
                }
            })
            .catch(err => {
                console.error('Error loading projects:', err);
            });
    }

    const handleImportProjectSelection = (projectValue: string) => {
        const selectedProject = state.importAvailableProjects.find((p) => p.value === projectValue);
        setState(prev => ({
            ...prev,
            importSelectedProject: selectedProject || null,
            importQuerySelectDlgOpen: true,
            importAvailableQueries: [],
            importSelectedQuery: null,
            importQueryLoading: true,
            importQueryError: '',
            importDetailsLoading: false,
            importDetailsError: '',
            importScriptSQL: '',
            importScriptValidation: '',
            importScriptPreValidation: '',
            importScriptTabIndex: 0,
        }));

        // Fetch queries for the selected project
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetClientQueriesFromProjectForDropDown?projectID=' + projectValue;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({
                        ...prev,
                        importAvailableQueries: res.results,
                        importQueryLoading: false
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        importQueryLoading: false,
                        importQueryError: 'Error loading queries: ' + (res.callStatusMessage || 'Unknown error')
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    importQueryLoading: false,
                    importQueryError: 'Error loading queries: ' + err.toString()
                }));
            });
    }

    const closeImportQuerySelectDialog = () => {
        setState(prev => ({
            ...prev,
            importQuerySelectDlgOpen: false,
            importAvailableQueries: [],
            importSelectedQuery: null,
            importQueryLoading: false,
            importQueryError: '',
            importDetailsLoading: false,
            importDetailsError: '',
            importScriptSQL: '',
            importScriptValidation: '',
            importScriptPreValidation: '',
            importScriptTabIndex: 0,
        }));
    }

    const fetchImportQueryDetails = async (projectID: string, clientQueryID: string) => {
        setState(prev => ({
            ...prev,
            importDetailsLoading: true,
            importDetailsError: '',
        }));

        try {
            const url = props.userInf.currProject.apiUrl + `/api/TMI/GetClientQueryDetailsFromProject?projectID=${projectID}&clientQueryID=${clientQueryID}`;
            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(r => r.json());

            if (res.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                setState(prev => ({ ...prev, importDetailsLoading: false }));
                return;
            }

            if (res.callStatus !== 'OK') {
                setState(prev => ({
                    ...prev,
                    importDetailsLoading: false,
                    importDetailsError: 'Error loading query details: ' + (res.callStatusMessage || 'Unknown error'),
                    importScriptSQL: '',
                    importScriptValidation: '',
                    importScriptPreValidation: '',
                }));
                return;
            }

            const details = Array.isArray(res.results)
                ? (res.results[0] || {})
                : (res.result || res.results || {});

            const sql = details.sql ?? details.SQL ?? '';
            const validation = details.validation ?? details.Validation ?? '';
            const preValidation = details.preValidation ?? details.PreValidation ?? details.Prevalidation ?? '';

            setState(prev => {
                const projectStillSelected = prev.importSelectedProject?.value === projectID;
                const queryStillSelected = prev.importSelectedQuery?.value === clientQueryID;
                if (!projectStillSelected || !queryStillSelected) {
                    return prev;
                }
                return {
                    ...prev,
                    importDetailsLoading: false,
                    importDetailsError: '',
                    importScriptSQL: String(sql),
                    importScriptValidation: String(validation),
                    importScriptPreValidation: String(preValidation),
                };
            });
        } catch (err) {
            setState(prev => ({
                ...prev,
                importDetailsLoading: false,
                importDetailsError: 'Error loading query details: ' + (err as any).toString(),
                importScriptSQL: '',
                importScriptValidation: '',
                importScriptPreValidation: '',
            }));
        }
    }

    const handleImportQuerySelected = async () => {
        const { importSelectedQuery } = state;
        if (!importSelectedQuery) return;

        setState(prev => ({
            ...prev,
            importQuerySelectDlgOpen: false,
            importImportDescription: importSelectedQuery.display
        }));
    }

    const closeImportFromProjectDialog = () => {
        setState(prev => ({
            ...prev,
            importFromProjectDlgOpen: false,
            importAvailableProjects: [],
            importSelectedProject: null,
            importQuerySelectDlgOpen: false,
            importAvailableQueries: [],
            importSelectedQuery: null,
            importLoading: false,
            importError: '',
            importQueryLoading: false,
            importQueryError: '',
            importDetailsLoading: false,
            importDetailsError: '',
            importImportDescription: '',
            importImportCode: '',
            importScriptSQL: '',
            importScriptValidation: '',
            importScriptPreValidation: '',
            importScriptTabIndex: 0,
            importImporting: false
        }));
    }

    const submitImportFromProject = async () => {
        const { importSelectedProject, importSelectedQuery } = state;
        if (!importSelectedProject || !importSelectedQuery) return;

        setState(prev => ({ ...prev, importImporting: true, importError: '' }));

        try {
            const projectID = importSelectedProject.value;
            const clientQueryID = importSelectedQuery.value;

            const url = props.userInf.currProject.apiUrl + `/api/TMI/ImportClientQuery?projectID=${projectID}&clientQueryID=${clientQueryID}`;
            const importRes = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(r => r.json());

            if (importRes.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                return;
            }

            if (importRes.callStatus !== 'OK') {
                setState(prev => ({ ...prev, importImporting: false, importError: 'Error importing query: ' + (importRes.callStatusMessage || 'Unknown error') }));
                return;
            }

            closeImportFromProjectDialog();
            fetchData();
        } catch (err) {
            setState(prev => ({ ...prev, importImporting: false, importError: 'Error importing query: ' + (err as any).toString() }));
        }
    }

    const submitCopyToNew = () => {
        const { copyToNewSourceItem, copyToNewDescription, copyToNewCode } = state;
        if (!copyToNewSourceItem) return;

        setState(prev => ({ ...prev, copyToNewSaving: true, copyToNewError: '' }));

        // First, fetch the source query's permissions
        Promise.all([
            fetch(props.userInf.currProject.apiUrl + '/api/TMI/GetSPLPageClientQueriesForDropDown?clientQueryID=' + copyToNewSourceItem.clientQueryID, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(r => r.json()),
            fetch(props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypeClientQueriesForDropDown?clientQueryID=' + copyToNewSourceItem.clientQueryID, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(r => r.json()),
            fetch(props.userInf.currProject.apiUrl + '/api/TMI/GetItemTypeClientQueriesForDropDown?clientQueryID=' + copyToNewSourceItem.clientQueryID, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(r => r.json()),
        ])
            .then(([splPagesRes, packlistTypesRes, itemTypesRes]) => {
                // Extract the selected items with their full data from the responses
                const splPagesSelectedData = (splPagesRes.callStatus === 'OK' && splPagesRes.result && Array.isArray(splPagesRes.result.splPages))
                    ? splPagesRes.result.splPages.map((item: any) => ({
                        securityID: item.securityID,
                        multiSelect: item.multiSelect === true
                    }))
                    : [];
                const packlistTypesSelected = (packlistTypesRes.callStatus === 'OK' && Array.isArray(packlistTypesRes.results))
                    ? packlistTypesRes.results.map((item: any) => typeof item === 'number' ? item : item.securityID)
                    : [];
                const itemTypesSelected = (itemTypesRes.callStatus === 'OK' && Array.isArray(itemTypesRes.results))
                    ? itemTypesRes.results.map((item: any) => typeof item === 'number' ? item : item.securityID)
                    : [];

                // Step 1: Create the new client query with scripts
                const newQueryData = {
                    Description: copyToNewDescription,
                    SQL: copyToNewSourceItem.sql ?? '',
                    ActionType: copyToNewSourceItem.actionType,
                    ClientQueryCode: copyToNewCode,
                    IsExcelViewable: copyToNewSourceItem.isExcelViewable,
                    InActive: copyToNewSourceItem.inActive,
                    ClientQueryValidationTypeID: copyToNewSourceItem.clientQueryValidationTypeID,
                    ClientQueryGrpId: copyToNewSourceItem.clientQueryGrpId,
                    PreValidation: copyToNewSourceItem.preValidation ?? '',
                    Validation: copyToNewSourceItem.validation ?? '',
                };

                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQuery';
                return fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify(newQueryData),
                })
                    .then(resp => resp.json())
                    .then(res => ({
                        res,
                        splPagesSelectedData,
                        packlistTypesSelected,
                        itemTypesSelected
                    }));
            })
            .then(({ res, splPagesSelectedData, packlistTypesSelected, itemTypesSelected }) => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeCopyToNewDialog();
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, copyToNewSaving: false, copyToNewError: 'Error creating copy: ' + res.callStatusMessage }));
                } else {
                    // Step 2: Get the new query ID from the response
                    const newClientQueryID = res.result;
                    if (!newClientQueryID) {
                        setState(prev => ({ ...prev, copyToNewSaving: false, copyToNewError: 'Error: Could not get new query ID' }));
                        return;
                    }

                    // Step 3: Copy parameters if any exist
                    const copyPromises: Promise<any>[] = [];

                    if (copyToNewSourceItem.parameters && copyToNewSourceItem.parameters.length > 0) {
                        copyToNewSourceItem.parameters.forEach(param => {
                            const paramUrl = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryParameter';
                            const paramData = {
                                ClientQueryParameterID: 0,
                                ClientQueryID: newClientQueryID,
                                Name: param.name,
                                DisplayName: param.displayName,
                                AttributeClassID: param.attributeClassID,
                                Required: param.required,
                                Visible: param.visible,
                                UseLookupValue: param.useLookupValue,
                                SortOrder: param.sortOrder,
                                LookupSql: param.lookupSql ?? '',
                                Validation: param.validation ?? '',
                                DefaultValueSql: param.defaultValueSql ?? '',
                            };
                            copyPromises.push(
                                fetch(paramUrl, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': 'Bearer ' + props.userInf.token,
                                    },
                                    body: JSON.stringify(paramData),
                                }).then(r => r.json())
                            );
                        });
                    }

                    // Step 4: Copy permissions (SPL Pages, Packlist Types, Item Types - but NOT User Roles)
                    copyPromises.push(
                        // Copy SPL Pages permissions
                        fetch(props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateSPLPageClientQuery', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + props.userInf.token,
                            },
                            body: JSON.stringify({
                                ClientQueryID: newClientQueryID,
                                SPLPages: splPagesSelectedData.map(item => ({
                                    SecurityID: item.securityID,
                                    MultiSelect: item.multiSelect
                                }))
                            })
                        }).then(r => r.json()),

                        // Copy Packlist Types permissions
                        fetch(props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryPackListType', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + props.userInf.token,
                            },
                            body: JSON.stringify({
                                Checked: packlistTypesSelected,
                                Selected: newClientQueryID
                            })
                        }).then(r => r.json()),

                        // Copy Item Types permissions
                        fetch(props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryItemType', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + props.userInf.token,
                            },
                            body: JSON.stringify({
                                Checked: itemTypesSelected,
                                Selected: newClientQueryID
                            })
                        }).then(r => r.json())
                    );

                    Promise.all(copyPromises)
                        .then(() => {
                            closeCopyToNewDialog();
                            fetchData();
                        })
                        .catch(err => {
                            setState(prev => ({ ...prev, copyToNewSaving: false, copyToNewError: 'Error copying permissions/parameters: ' + err.toString() }));
                        });
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, copyToNewSaving: false, copyToNewError: 'Error fetching source permissions: ' + err.toString() }));
            });
    }

    const onMenuFocus = () => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
    }

    const onMenuBlur = () => {
        menuBlurTimeoutRef.current = setTimeout(() => closeRowMenu(), 100);
    }

    // When menuAnchor exists but menuPosition is null, render the menu hidden
    // and measure its real size, then compute the final position and show it.
    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                // Clamp horizontally
                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                // If not enough room below, flip above and anchor bottom-left to the button
                if (top + menuH > scrollY + winH) {
                    // Account for the anchor (button) height when flipping above so the
                    // menu bottom aligns with the top of the button.
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    left = Math.round(actualAnchorRect.left + scrollX);
                    if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (left < scrollX + 8) left = Math.round(scrollX + 8);
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch (e) {
                // ignore measurement errors
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    // Run measurement/flip when an anchor and a menuPosition exist (we may adjust based on real menu size)
    useEffect(() => {
        if (!state.menuAnchor || !state.menuPosition) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const snap = state.menuAnchorRect;
                const anchorRect = snap ? snap as any : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const menuW = menuRect.width;
                const menuH = menuRect.height;

                const belowTop = Math.round(anchorRect.bottom + scrollY);
                const anchorLeftAbs = Math.round(anchorRect.left + scrollX);

                let newLeft = state.menuPosition!.left;
                let newTop = state.menuPosition!.top;

                // If currently positioned below the anchor (top at/after belowTop) but it overflows the viewport bottom, flip above
                if (state.menuPosition!.top >= belowTop - 2 && (menuRect.bottom > winH)) {
                    // Anchor bottom-left should align with menu bottom-left: left = anchor.left
                    newLeft = anchorLeftAbs;
                    // Compute flipped top so menu bottom aligns with the top of the button
                    // (account for the button height so menu doesn't render too low).
                    newTop = Math.round(anchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    // Clamp horizontally if necessary
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                } else {
                    // Otherwise, ensure menu is clamped within viewport horizontally/vertically
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                    if (newTop + menuH > scrollY + winH) newTop = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    if (newTop < scrollY + 8) newTop = Math.round(scrollY + 8);
                }

                // Debugging info removed

                if (newLeft !== state.menuPosition!.left || newTop !== state.menuPosition!.top) {
                    setState(prev => ({ ...prev, menuPosition: { left: newLeft, top: newTop } }));
                }
            } catch (e) {
                // ignore measurement errors
            }
        }, 30);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Node;
            // Check if click is outside the menu ref and outside the anchor button
            if (menuRef.current && !menuRef.current.contains(target) &&
                state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [state.menuDataItem, state.menuAnchor]);

    const openNewClientQueryDlg = () => {
        setState(prev => ({
            ...prev,
            newClientQueryDlgOpen: true,
            newClientQueryDlgSaving: false,
            newClientQueryDlgError: '',
            newClientQueryDlgDescription: '',
            newClientQueryDlgSQL: '',
            newClientQueryDlgActionType: '',
            newClientQueryDlgClientQueryCode: '',
            newClientQueryDlgIsExcelViewable: false,
            newClientQueryDlgIsActive: true,
            newClientQueryDlgClientQueryValidationTypeID: '',
            newClientQueryDlgClientQueryGrpId: '',
        }));
    }

    const closeNewClientQueryDlg = () => {
        setState(prev => ({ ...prev, newClientQueryDlgOpen: false }));
    }



    const submitNewClientQuery = () => {
        const {
            newClientQueryDlgDescription,
            newClientQueryDlgSQL,
            newClientQueryDlgActionType,
            newClientQueryDlgClientQueryCode,
            newClientQueryDlgIsExcelViewable,
            newClientQueryDlgIsActive,
            newClientQueryDlgClientQueryValidationTypeID,
            newClientQueryDlgClientQueryGrpId,
        } = state;
        setState(prev => ({ ...prev, newClientQueryDlgSaving: true, newClientQueryDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQuery';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                Description: newClientQueryDlgDescription,
                SQL: newClientQueryDlgSQL,
                ActionType: newClientQueryDlgActionType,
                ClientQueryCode: newClientQueryDlgClientQueryCode,
                IsExcelViewable: newClientQueryDlgIsExcelViewable,
                InActive: !newClientQueryDlgIsActive,
                ClientQueryValidationTypeID: newClientQueryDlgClientQueryValidationTypeID ? parseInt(newClientQueryDlgClientQueryValidationTypeID) : 0,
                ClientQueryGrpId: newClientQueryDlgClientQueryGrpId ? parseInt(newClientQueryDlgClientQueryGrpId) : null,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newClientQueryDlgOpen: false, newClientQueryDlgSaving: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, newClientQueryDlgSaving: false, newClientQueryDlgError: 'Error creating Query: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, newClientQueryDlgOpen: false, newClientQueryDlgSaving: false }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newClientQueryDlgSaving: false, newClientQueryDlgError: 'Error creating Query: ' + err.toString() }));
            });
    }

    const BooleanCell = (props: GridCellProps) => {
        const value: boolean = props.dataItem[props.field!] ?? false;
        const inEdit: boolean = props.dataItem[EDIT_FIELD];

        if (inEdit) {
            return (
                <td style={{ textAlign: 'center' }}>
                    <input
                        type="checkbox"
                        defaultChecked={value}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => {
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === props.dataItem.clientQueryID ? { ...item, [props.field!]: e.target.checked } : item) }));
                        }}
                    />
                </td>
            );
        }

        return (
            <td style={{ textAlign: 'center' }}>
                {value ? '✓' : ''}
            </td>
        );
    }

    const IsActiveCell = (props: GridCellProps) => {
        const inActive: boolean = props.dataItem[props.field!] ?? true;
        const isActive = !inActive;
        const inEdit: boolean = props.dataItem[EDIT_FIELD];

        if (inEdit) {
            return (
                <td style={{ textAlign: 'center' }}>
                    <input
                        type="checkbox"
                        checked={isActive}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => {
                            const newInActive = !e.target.checked;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === props.dataItem.clientQueryID ? { ...item, inActive: newInActive } : item) }));
                        }}
                    />
                </td>
            );
        }

        return (
            <td style={{ textAlign: 'center' }}>
                {isActive ? '✓' : ''}
            </td>
        );
    }

    const ActionTypeCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];

        if (inEdit) {
            return (
                <td style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={val}
                        onChange={e => {
                            const newVal = e.target.value;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === props.dataItem.clientQueryID ? { ...item, [props.field!]: newVal } : item) }));
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {state.actionTypesDropdown.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.display}
                            </option>
                        ))}
                    </select>
                </td>
            );
        }

        // Display mode: show the action type display name instead of just the value
        const typeDisplay = state.actionTypesDropdown.find(t => t.value === val)?.display || val;
        return <td title={String(typeDisplay)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeDisplay}</td>;
    }

    const ValidationTypeCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];

        if (inEdit) {
            return (
                <td style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={val}
                        onChange={e => {
                            const newVal = e.target.value;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === props.dataItem.clientQueryID ? { ...item, [props.field!]: newVal } : item) }));
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {state.validationTypesDropdown.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.display}
                            </option>
                        ))}
                    </select>
                </td>
            );
        }

        // Display mode: show the validation type display name instead of just the value
        const typeDisplay = state.validationTypesDropdown.find(t => String(t.value) === String(val))?.display || val || '';
        return <td title={String(typeDisplay)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeDisplay}</td>;
    }

    const QueryGroupCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];

        if (inEdit) {
            return (
                <td style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={val}
                        onChange={e => {
                            const newVal = e.target.value;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientQueryID === props.dataItem.clientQueryID ? { ...item, [props.field!]: newVal } : item) }));
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {state.clientQueryGroupsDropdown.map(group => (
                            <option key={group.value} value={group.value}>
                                {group.display}
                            </option>
                        ))}
                    </select>
                </td>
            );
        }

        // Display mode: show the query group display name instead of just the value
        const groupDisplay = state.clientQueryGroupsDropdown.find(g => String(g.value) === String(val))?.display || val || '';
        return <td title={String(groupDisplay)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{groupDisplay}</td>;
    }

    const IDCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.mainGridSelectedId === props.dataItem.clientQueryID;
        return (
            <td
                title={String(val)}
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    backgroundColor: isSelected ? '#FF9800' : (inEdit ? '#f5f5f5' : 'transparent'),
                    color: isSelected ? '#ffffff' : (inEdit ? '#999' : 'inherit'),
                    fontStyle: inEdit ? 'italic' : 'normal',
                    transition: 'all 0.2s ease'
                }}
            >
                {val}
            </td>
        );
    }

    // Header renderers ref for caching (stateRef is declared earlier, near the state hook)
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const HEADER_RENDERER_VERSION = 'v2';

    const buildHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <div className="tmi-header-filter">
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input type="text" className="k-input k-input-sm" value={val} onChange={(e) => handleHeaderFilterChange(field, e)} onFocus={() => handleHeaderFocus(field)} onBlur={() => handleHeaderBlur()} onMouseDown={(e) => { e.stopPropagation(); }} />
                        </div>
                    </div>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const buildBooleanHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                return (
                    <div className="tmi-header-filter">
                        <div className="k-link" aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">↕</span>
                            </label>
                            <select className="k-input k-input-sm" value={val} onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)} onFocus={() => handleHeaderFocus(field)} onBlur={() => handleHeaderBlur()} onMouseDown={(e) => { e.stopPropagation(); }}>
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </div>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const buildLookupHeaderCellRenderer = (field: string, title: string, optionsSelector: (s: ClientQueriesState) => LookupListItem[]) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_lookup_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const options = optionsSelector(stateRef.current);
                return (
                    <div className="tmi-header-filter">
                        <div className="k-link" aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">↕</span>
                            </label>
                            <select className="k-input k-input-sm" value={val} onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)} onFocus={() => handleHeaderFocus(field)} onBlur={() => handleHeaderBlur()} onMouseDown={(e) => { e.stopPropagation(); }}>
                                <option value="">All</option>
                                {options.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const createSimpleHeaderCell = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <div className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '4px 6px',
                            boxSizing: 'border-box',
                            fontSize: '12px',
                            lineHeight: '1.3',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            flex: 1,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}>
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </div>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const HamburgerHeaderCell = () => {
        return (
            <div className="tmi-header-action-cell tmi-command-column">
                <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={fetchData} disabled={loading} title="Refresh">
                    <span className="k-icon k-font-icon k-i-reload" />
                </button>
            </div>
        );
    }

    const HamburgerCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td style={{ textAlign: 'center', padding: '2px' }}>
                {inEdit ? (
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-success"
                        title="Save"
                        onClick={() => handleSave(dataItem)}
                    >
                        <span className="k-icon k-font-icon k-i-check" />
                    </button>
                ) : (
                    <button
                        className="k-button k-button-sm k-rounded-md"
                        title="Actions"
                        onClick={e => openRowMenu(dataItem as ClientQueryRow, e.currentTarget)}
                    >
                        <span className="k-icon k-font-icon k-i-more-vertical" />
                    </button>
                )}
            </td>
        );
    }

    const CommandHeaderCell = () => {
        return (
            <div className="tmi-command-header-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="New Query"
                    onClick={openNewClientQueryDlg}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="Import from Another Project"
                    onClick={openImportFromProjectDialog}
                >
                    <span className="k-icon k-font-icon k-i-download" />
                </button>
            </div>
        );
    }

    const CommandCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td className="tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={handleCancel} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={() => openDeleteConfirm(dataItem as ClientQueryRow)} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={() => handleEnterEdit(dataItem)} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={() => openDeleteConfirm(dataItem as ClientQueryRow)} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    }

    const {
        data,
        loading,
        errorMsg,
        sort,
        filterTexts,
        editItemId,
        selectedIds,
    } = state;

    // Manual filtering logic
    const filteredData = Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)
        ? data
        : data.filter(item => {
            return Object.entries(filterTexts).every(([f, q]) => {
                if (!q) return true;
                const val = (item as any)[f];
                const ql = q.trim().toLowerCase();
                const exactMatchDropdownFields = new Set(['actionType', 'clientQueryValidationTypeID', 'clientQueryGrpId']);
                if (ql === 'true' || ql === 'false') {
                    return String(val ?? '').toLowerCase() === ql;
                }
                if (exactMatchDropdownFields.has(f)) {
                    return String(val ?? '').toLowerCase() === ql;
                }
                return String(val ?? '').toLowerCase().includes(ql);
            });
        });

    const sortedData = orderBy(filteredData, sort);

    const gridData = sortedData.map(item => ({
        ...item,
        [EDIT_FIELD]: item.clientQueryID === editItemId,
        [SELECTED_FIELD]: selectedIds.has(item.clientQueryID),
    }));

    const permissionsSplPagesGridData = orderBy(
        state.permissionsSplPagesData.map(item => ({
            ...item,
            selected: state.permissionsSplPagesSelected.includes(Number(item.value)),
        })),
        state.permissionsSplPagesSort
    );

    const permissionsUserRolesGridData = orderBy(
        state.permissionsUserRolesData.map(item => ({
            ...item,
            selected: state.permissionsUserRolesSelected.includes(Number(item.value)),
        })),
        state.permissionsUserRolesSort
    );

    const permissionsPacklistTypesGridData = orderBy(
        state.permissionsPacklistTypesData.map(item => ({
            ...item,
            selected: state.permissionsPacklistTypesSelected.includes(Number(item.value)),
        })),
        state.permissionsPacklistTypesSort
    );

    const permissionsItemTypesGridData = orderBy(
        state.permissionsItemTypesData.map(item => ({
            ...item,
            selected: state.permissionsItemTypesSelected.includes(Number(item.value)),
        })),
        state.permissionsItemTypesSort
    );

    const itemTypePacklistsGridData = orderBy(
        state.itemTypePacklistsData.map(item => ({
            ...item,
            selected: state.itemTypePacklistsSelected.includes(Number(item.value)),
        })),
        state.itemTypePacklistsSort
    );

    // Shared editable text cell used by the Parameters dialog's Sort Order/Name/Display Name
    // columns. It is memoized with useCallback so the GridColumn's `cell` prop keeps the same
    // function identity across renders (reading live state via stateRef instead of closing over
    // `state` directly). Previously each column defined its own inline arrow function in JSX,
    // which is recreated on every render; Kendo's Grid then saw a "new" cell renderer and
    // remounted the <input>, which could drop focus/in-progress edits.
    const parametersEditableTextCell = useCallback((props: GridCellProps) => {
        const isSelected = stateRef.current.parametersGridSelectedId === props.dataItem.clientQueryParameterID;
        const val = props.dataItem[props.field!] || '';
        return (
            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                {props.dataItem.inEdit ? (
                    <input
                        type="text"
                        defaultValue={val}
                        style={{ width: '100%' }}
                        autoFocus={props.field === 'sortOrder'}
                    />
                ) : (
                    val
                )}
            </td>
        );
    }, []);

    // Shared editable checkbox cell used by the Parameters dialog's Required/Visible/Use Lookup
    // Value columns. Memoized for the same reason as parametersEditableTextCell above.
    const parametersEditableCheckboxCell = useCallback((props: GridCellProps) => {
        const isSelected = stateRef.current.parametersGridSelectedId === props.dataItem.clientQueryParameterID;
        const val: boolean = props.dataItem[props.field!] || false;
        return (
            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                {props.dataItem.inEdit ? (
                    <input type="checkbox" defaultChecked={val} />
                ) : (
                    val ? '✓' : ''
                )}
            </td>
        );
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {loading && <span className="k-icon k-font-icon k-i-loading" />}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                </div>
            </div>
            {errorMsg && <div style={{ color: 'red', marginBottom: '8px' }}>{errorMsg}</div>}
            <Grid
                className="tmi-grid ClientQueries-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey={DATA_ITEM_KEY}
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
                onRowClick={handleMainGridRowClick}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn headerCell={createSimpleHeaderCell('clientQueryID', 'ID')} cell={IDCell} field='clientQueryID' title='ID' filterable={false} sortable width={60} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('clientQueryCode', 'Code (DO NOT CHANGE)')} field='clientQueryCode' title='Code (DO NOT CHANGE)' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('description', 'Description')} field='description' title='Description' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildLookupHeaderCellRenderer('actionType', 'Action Type', s => s.actionTypesDropdown)} cell={ActionTypeCell} field='actionType' title='Action Type' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildLookupHeaderCellRenderer('clientQueryValidationTypeID', 'Validation Type', s => s.validationTypesDropdown)} cell={ValidationTypeCell} field='clientQueryValidationTypeID' title='Validation Type' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildLookupHeaderCellRenderer('clientQueryGrpId', 'Query Group', s => s.clientQueryGroupsDropdown)} cell={QueryGroupCell} field='clientQueryGrpId' title='Query Group' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('isExcelViewable', 'Excel Viewable')} cell={BooleanCell} field='isExcelViewable' title='Excel Viewable' filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('inActive', 'Is Active')} cell={IsActiveCell} field='inActive' title='Is Active' width={100} filterable={false} sortable={false} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} filterable={false} sortable={false} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>



            {state.menuDataItem && state.menuAnchor && (
                <div
                    ref={menuRef}
                    onFocus={onMenuFocus}
                    onBlur={onMenuBlur}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition?.left ?? 0,
                        top: state.menuPosition?.top ?? 0,
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 10000,
                        minWidth: '200px',
                        visibility: state.menuPosition ? 'visible' : 'hidden',
                    }}
                    tabIndex={0}
                >
                    <button
                        style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 16px',
                            textAlign: 'left',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: '14px',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => state.menuDataItem && openScriptEditDialog(state.menuDataItem)}
                    >
                        Client Query Scripts and Parameters
                    </button>
                    <button
                        style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 16px',
                            textAlign: 'left',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderTop: '1px solid #e0e0e0',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => state.menuDataItem && openPermissionsDialog(state.menuDataItem)}
                    >
                        Permissions
                    </button>
                    <button
                        style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 16px',
                            textAlign: 'left',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderTop: '1px solid #e0e0e0',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => state.menuDataItem && openCopyToNewDialog(state.menuDataItem)}
                    >
                        Copy to New CQ
                    </button>
                </div>
            )}

            {state.deleteConfirmItem && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete <strong>{state.deleteConfirmItem?.description}</strong>?
                    </p>
                    {state.deleteErrorMsg && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.deleteErrorMsg}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmDelete}
                            disabled={state.deleteInProgress}
                        >
                            {state.deleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.newClientQueryDlgOpen && (
                <Dialog
                    title="New Client Query"
                    onClose={closeNewClientQueryDlg}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', maxHeight: '500px', overflowY: 'auto' }}>
                        {state.newClientQueryDlgError && (
                            <div style={{ color: 'red' }}>{state.newClientQueryDlgError}</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Code</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgClientQueryCode}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgClientQueryCode: e.target.value }))}
                                style={{ width: '100%' }}
                                maxLength={100}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Description</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgDescription}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgDescription: e.target.value }))}
                                style={{ width: '100%' }}
                                maxLength={100}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Action Type</label>
                            <select
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgActionType}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgActionType: e.target.value }))}
                                style={{ width: '100%' }}
                            >
                                <option value="">-- Select --</option>
                                {state.actionTypesDropdown.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Validation Type</label>
                            <select
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgClientQueryValidationTypeID}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgClientQueryValidationTypeID: e.target.value }))}
                                style={{ width: '100%' }}
                            >
                                <option value="">-- Select --</option>
                                {state.validationTypesDropdown.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Query Group</label>
                            <select
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgClientQueryGrpId}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgClientQueryGrpId: e.target.value }))}
                                style={{ width: '100%' }}
                            >
                                <option value="">-- Select --</option>
                                {state.clientQueryGroupsDropdown.map(group => (
                                    <option key={group.value} value={group.value}>
                                        {group.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>SQL</label>
                            <textarea
                                className="k-input k-input-md k-rounded-md"
                                value={state.newClientQueryDlgSQL}
                                onChange={e => setState(prev => ({ ...prev, newClientQueryDlgSQL: e.target.value }))}
                                style={{ width: '100%', minHeight: '100px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
                            <label style={{ fontWeight: 'bold' }}>Options</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newClientQueryDlgIsExcelViewable}
                                    onChange={e => setState(prev => ({ ...prev, newClientQueryDlgIsExcelViewable: e.target.checked }))}
                                />
                                <label>Excel Viewable</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newClientQueryDlgIsActive}
                                    onChange={e => setState(prev => ({ ...prev, newClientQueryDlgIsActive: e.target.checked }))}
                                />
                                <label>Active</label>
                            </div>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeNewClientQueryDlg} disabled={state.newClientQueryDlgSaving}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitNewClientQuery}
                            disabled={state.newClientQueryDlgSaving}
                        >
                            {state.newClientQueryDlgSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Create'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.scriptEditItem && (
                <Dialog
                    title="Client Query Scripts and Parameters"
                    onClose={() => { if (!state.scriptEditSaving) closeScriptEditDialog(); }}
                    width="90vw"
                    height="84vh"
                    className="cq-editor-dialog"
                >
                    <div className="cq-editor-summary">
                        Query ID: {state.scriptEditItem.clientQueryID} | Code: {state.scriptEditItem.clientQueryCode} | {state.scriptEditItem.description}
                    </div>
                    <div className="cq-editor-panes">
                    <section className="cq-editor-scripts" aria-label="Client query scripts">
                        <h3>Scripts</h3>
                        {state.scriptEditError && (
                            <div style={{ color: 'red', padding: '8px', marginBottom: '8px' }}>{state.scriptEditError}</div>
                        )}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
                            <TabStrip selected={state.scriptEditTabIndex} onSelect={(e) => setState(prev => ({ ...prev, scriptEditTabIndex: e.selected }))} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', margin: 0, padding: 0 }}>

                                <TabStripTab title="SQL Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        aria-label="SQL Script"
                                        value={state.scriptEditSQL}
                                        onChange={e => setState(prev => ({ ...prev, scriptEditSQL: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>
                                <TabStripTab title="Validation Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        aria-label="Validation Script"
                                        value={state.scriptEditValidation}
                                        onChange={e => setState(prev => ({ ...prev, scriptEditValidation: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>
                                <TabStripTab title="Pre-Validation Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        aria-label="Pre-Validation Script"
                                        value={state.scriptEditPreValidation}
                                        onChange={e => setState(prev => ({ ...prev, scriptEditPreValidation: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>

                            </TabStrip>
                        </div>
                        <div className="cq-editor-script-actions">
                            <button
                                className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                onClick={submitScriptEdit}
                                disabled={state.scriptEditSaving}
                            >
                                {state.scriptEditSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Save Scripts'}
                            </button>
                        </div>
                    </section>
                    <section className="cq-editor-parameters" aria-label="Client query parameters">
                        <h3>Parameters</h3>
                        <div className="cq-editor-save-hint">Save parameter changes using the check mark on each row. Parameter changes are saved separately from scripts.</div>
                        {state.parametersError && (
                            <div style={{ color: 'red' }}>{state.parametersError}</div>
                        )}
                        {state.parametersLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                            </div>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                                <Grid
                                    style={{ flex: 1 }}
                                    data={state.parametersList}
                                    dataItemKey="clientQueryParameterID"
                                    editField="inEdit"
                                    onItemChange={(e) => {
                                        const updated = state.parametersList.map(p =>
                                            p.clientQueryParameterID === e.dataItem.clientQueryParameterID ? e.dataItem : p
                                        );
                                        setState(prev => ({ ...prev, parametersList: updated }));
                                    }}
                                    onRowClick={handleParametersGridRowClick}
                                >
                                    <GridColumn field='clientQueryParameterID' title='ID' hidden={true} filterable={false} />
                                    <GridColumn field='sortOrder' title='Sort Order' width={100} cell={parametersEditableTextCell} />
                                    <GridColumn field='name' title='Name' width={160} cell={parametersEditableTextCell} />
                                    <GridColumn field='displayName' title='Display Name' width={160} cell={parametersEditableTextCell} />
                                    <GridColumn field='attributeClassID' title='Attribute Class ID' width={160} cell={(props) => {
                                        const isSelected = state.parametersGridSelectedId === props.dataItem.clientQueryParameterID;
                                        return (
                                        <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                            {props.dataItem.inEdit ? (
                                                <select
                                                    defaultValue={props.dataItem.attributeClassID || ''}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="">-- Select --</option>
                                                    {state.attributeClassesDropdown.map(item => (
                                                        <option key={item.value} value={item.value}>
                                                            {item.display}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                state.attributeClassesDropdown.find(item => String(item.value) === String(props.dataItem.attributeClassID))?.display || props.dataItem.attributeClassID
                                            )}
                                        </td>
                                        );
                                    }} />
                                    <GridColumn field='required' title='Required' width={100} cell={parametersEditableCheckboxCell} />
                                    <GridColumn field='visible' title='Visible' width={100} cell={parametersEditableCheckboxCell} />
                                    <GridColumn field='useLookupValue' title='Use Lookup Value' width={100} cell={parametersEditableCheckboxCell} />
                                    <GridColumn
                                        width={40}
                                        filterable={false}
                                        sortable={false}
                                        cell={(props) => (
                                            <td style={{ padding: 0, textAlign: 'center' }}>
                                                <button
                                                    className="k-button k-button-sm k-rounded-md k-button-solid-secondary"
                                                    style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                    onClick={() => {
                                                        setState(prev => ({
                                                            ...prev,
                                                            parameterDetailsEditingId: props.dataItem.clientQueryParameterID,
                                                            parameterDetailsDialogOpen: true,
                                                            parameterDetailsItem: props.dataItem,
                                                            parameterDetailsLookupSql: props.dataItem.lookupSql || '',
                                                            parameterDetailsValidation: props.dataItem.validation || '',
                                                            parameterDetailsHelpDescription: props.dataItem.helpDescription || '',
                                                            parameterDetailsDefaultValue: props.dataItem.defaultValueSql || ''
                                                        }));
                                                    }}
                                                    title="Lookup, Validation, Help Description, Default Value"
                                                >
                                                    <span className="k-icon k-font-icon k-i-menu" />
                                                </button>
                                            </td>
                                        )}
                                    />
                                    <GridColumn
                                        width={80}
                                        filterable={false}
                                        sortable={false}
                                        cell={(props) => (
                                            <td style={{ padding: 0 }}>
                                                {props.dataItem.inEdit ? (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                            onClick={() => {
                                                                const updated = props.dataItem.clientQueryParameterID === 0
                                                                    ? state.parametersList.filter(p => p.clientQueryParameterID !== 0)
                                                                    : state.parametersList.map(p =>
                                                                        p.clientQueryParameterID === props.dataItem.clientQueryParameterID 
                                                                            ? { ...p, inEdit: false }
                                                                            : p
                                                                    );
                                                                setState(prev => ({ ...prev, parametersList: updated }));
                                                            }}
                                                            title="Cancel"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-cancel" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                            onClick={(e) => {
                                                                // Read values from the uncontrolled inputs in this row
                                                                const row = e.currentTarget.closest('tr');
                                                                const inputs = row?.querySelectorAll('input[type="text"]') || [];
                                                                const checkboxes = row?.querySelectorAll('input[type="checkbox"]') || [];
                                                                const select = row?.querySelector('select') as HTMLSelectElement;

                                                                const sortOrderInput = inputs[0] as HTMLInputElement;
                                                                const nameInput = inputs[1] as HTMLInputElement;
                                                                const displayNameInput = inputs[2] as HTMLInputElement;
                                                                const requiredCheckbox = checkboxes[0] as HTMLInputElement;
                                                                const visibleCheckbox = checkboxes[1] as HTMLInputElement;
                                                                const useLookupValueCheckbox = checkboxes[2] as HTMLInputElement;

                                                                saveParameterRow(props.dataItem, {
                                                                    sortOrder: parseInt(sortOrderInput?.value) || 0,
                                                                    name: nameInput?.value || '',
                                                                    displayName: displayNameInput?.value || '',
                                                                    attributeClassID: parseInt(select?.value) || 0,
                                                                    required: requiredCheckbox?.checked || false,
                                                                    visible: visibleCheckbox?.checked || false,
                                                                    useLookupValue: useLookupValueCheckbox?.checked || false,
                                                                });
                                                            }}
                                                            title="Save"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-check" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-base"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                            onClick={() => {
                                                                const updated = state.parametersList.map(p =>
                                                                    p.clientQueryParameterID === props.dataItem.clientQueryParameterID 
                                                                        ? { ...p, inEdit: true }
                                                                        : p
                                                                );
                                                                setState(prev => ({ ...prev, parametersList: updated }));
                                                            }}
                                                            title="Edit"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-edit" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-error"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: '0 0 0 4px', padding: '6px 8px' }}
                                                            onClick={() => {
                                                                setState(prev => ({
                                                                    ...prev,
                                                                    parameterDeleteConfirmItem: props.dataItem
                                                                }));
                                                            }}
                                                            title="Delete"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-trash" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    />
                                </Grid>
                                <button
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                                    style={{ marginTop: '8px' }}
                                    onClick={() => {
                                        const newParam: ClientQueryParameter = {
                                            clientQueryParameterID: 0,
                                            clientQueryID: state.parametersItem?.clientQueryID || 0,
                                            name: '',
                                            displayName: '',
                                            attributeClassID: 0,
                                            required: false,
                                            visible: false,
                                            useLookupValue: false,
                                            sortOrder: 0,
                                            helpDescription: null,
                                            lookupSql: null,
                                            validation: null,
                                            defaultValueSql: null,
                                            inEdit: true
                                        };
                                        setState(prev => ({ ...prev, parametersList: [...prev.parametersList, newParam] }));
                                    }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" style={{ marginRight: '4px' }} />
                                    Add Parameter
                                </button>
                            </div>
                        )}
                    </section>
                    </div>
                    <DialogActionsBar layout="end">
                        <button className="k-button k-button-md k-rounded-md" onClick={closeScriptEditDialog} disabled={state.scriptEditSaving}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.parameterDeleteConfirmItem && (
                <Dialog
                    title="Confirm Delete Parameter"
                    onClose={() => setState(prev => ({ ...prev, parameterDeleteConfirmItem: null }))}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete parameter <strong>{state.parameterDeleteConfirmItem?.displayName || state.parameterDeleteConfirmItem?.name}</strong>?
                    </p>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={() => setState(prev => ({ ...prev, parameterDeleteConfirmItem: null }))} disabled={state.parameterDeleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={() => {
                                if (!state.parameterDeleteConfirmItem) return;
                                const paramId = state.parameterDeleteConfirmItem.clientQueryParameterID;
                                
                                setState(prev => ({ ...prev, parameterDeleteInProgress: true }));

                                const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteClientQueryParameter';
                                fetch(url, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': 'Bearer ' + props.userInf.token,
                                    },
                                    body: JSON.stringify(paramId),
                                })
                                    .then(resp => resp.json())
                                    .then(res => {
                                        if (res.callStatus === 'UNAUTH') {
                                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                                            props.updateUserInfo(uInf);
                                            setState(prev => ({ ...prev, parameterDeleteConfirmItem: null, parameterDeleteInProgress: false }));
                                        } else if (res.callStatus !== 'OK') {
                                            alert('Error deleting parameter: ' + res.callStatusMessage);
                                            setState(prev => ({ ...prev, parameterDeleteInProgress: false }));
                                        } else {
                                            const filtered = state.parametersList.filter(p => 
                                                p.clientQueryParameterID !== paramId
                                            );
                                            setState(prev => ({
                                                ...prev,
                                                parametersList: filtered,
                                                parameterDeleteConfirmItem: null,
                                                parameterDeleteInProgress: false
                                            }));
                                        }
                                    })
                                    .catch(err => {
                                        alert('Error deleting parameter: ' + err.toString());
                                        setState(prev => ({ ...prev, parameterDeleteInProgress: false }));
                                    });
                            }}
                            disabled={state.parameterDeleteInProgress}
                        >
                            {state.parameterDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.parameterDetailsDialogOpen && state.parameterDetailsItem && (
                <Dialog
                    title={`Parameter Details - ${state.parameterDetailsItem?.name}`}
                    onClose={() => {
                        setState(prev => ({
                            ...prev,
                            parameterDetailsEditingId: null,
                            parameterDetailsDialogOpen: false,
                            parameterDetailsItem: null,
                            parameterDetailsLookupSql: '',
                            parameterDetailsValidation: '',
                            parameterDetailsHelpDescription: '',
                            parameterDetailsDefaultValue: '',
                            parameterDetailsSelectedTab: 0
                        }));
                    }}
                    width={900}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '600px' }}>
                        <TabStrip
                            selected={state.parameterDetailsSelectedTab}
                            onSelect={(e) => setState(prev => ({ ...prev, parameterDetailsSelectedTab: e.selected }))}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                        >
                            <TabStripTab title="Lookup SQL">
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}>
                                    <textarea
                                        key={`lookupSql-${state.parameterDetailsEditingId}`}
                                        data-field="lookupSql"
                                        style={{
                                            width: '820px',
                                            height: '520px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            border: 'none',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                            overflowY: 'auto'
                                        }}
                                    >{state.parameterDetailsLookupSql}</textarea>
                                </div>
                            </TabStripTab>
                            <TabStripTab title="Validation">
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}>
                                    <textarea
                                        key={`validation-${state.parameterDetailsEditingId}`}
                                        data-field="validation"
                                        style={{
                                            width: '820px',
                                            height: '520px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            border: 'none',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                            overflowY: 'auto'
                                        }}
                                    >{state.parameterDetailsValidation}</textarea>
                                </div>
                            </TabStripTab>
                            <TabStripTab title="Help Description">
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}>
                                    <textarea
                                        key={`helpDescription-${state.parameterDetailsEditingId}`}
                                        data-field="helpDescription"
                                        style={{
                                            width: '820px',
                                            height: '520px',
                                            fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
                                            fontSize: '12px',
                                            padding: '8px',
                                            border: 'none',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                            overflowY: 'auto'
                                        }}
                                    >{state.parameterDetailsHelpDescription}</textarea>
                                </div>
                            </TabStripTab>
                            <TabStripTab title="Default Value">
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}>
                                    <textarea
                                        key={`defaultValue-${state.parameterDetailsEditingId}`}
                                        data-field="defaultValue"
                                        style={{
                                            width: '820px',
                                            height: '520px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            border: 'none',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                            overflowY: 'auto'
                                        }}
                                    >{state.parameterDetailsDefaultValue}</textarea>
                                </div>
                            </TabStripTab>
                        </TabStrip>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={() => {
                            setState(prev => ({
                                ...prev,
                                parameterDetailsEditingId: null,
                                parameterDetailsDialogOpen: false,
                                parameterDetailsItem: null,
                                parameterDetailsLookupSql: '',
                                parameterDetailsValidation: '',
                                parameterDetailsHelpDescription: '',
                                parameterDetailsDefaultValue: ''
                            }));
                        }}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={() => {
                                if (!state.parameterDetailsItem || !state.parametersItem) return;

                                                // Read values from uncontrolled textareas
                                                const lookupSqlTextarea = document.querySelector('textarea[data-field="lookupSql"]') as HTMLTextAreaElement;
                                                const validationTextarea = document.querySelector('textarea[data-field="validation"]') as HTMLTextAreaElement;
                                                const helpDescriptionTextarea = document.querySelector('textarea[data-field="helpDescription"]') as HTMLTextAreaElement;
                                                const defaultValueTextarea = document.querySelector('textarea[data-field="defaultValue"]') as HTMLTextAreaElement;

                                                // Make API call to save parameter details
                                                const paramData = {
                                                    ClientQueryParameterID: state.parameterDetailsItem.clientQueryParameterID,
                                                    ClientQueryID: state.parametersItem.clientQueryID,
                                                    Name: state.parameterDetailsItem.name,
                                                    DisplayName: state.parameterDetailsItem.displayName,
                                                    AttributeClassID: state.parameterDetailsItem.attributeClassID,
                                                    Required: state.parameterDetailsItem.required,
                                                    Visible: state.parameterDetailsItem.visible,
                                                    UseLookupValue: state.parameterDetailsItem.useLookupValue,
                                                    SortOrder: state.parameterDetailsItem.sortOrder,
                                                    HelpDescription: helpDescriptionTextarea?.value || '',
                                                    LookupSql: lookupSqlTextarea?.value || '',
                                                    Validation: validationTextarea?.value || '',
                                                    DefaultValueSql: defaultValueTextarea?.value || '',
                                                };

                                                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateClientQueryParameter';
                                                fetch(url, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': 'Bearer ' + props.userInf.token,
                                    },
                                    body: JSON.stringify(paramData),
                                })
                                    .then(resp => resp.json())
                                    .then(res => {
                                        if (res.callStatus === 'UNAUTH') {
                                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                                            props.updateUserInfo(uInf);
                                        } else if (res.callStatus !== 'OK') {
                                            alert('Error saving parameter: ' + res.callStatusMessage);
                                        } else {
                                            // Update the parameter in the parametersList
                                            const updated = state.parametersList.map(p =>
                                                p.clientQueryParameterID === state.parameterDetailsItem?.clientQueryParameterID
                                                    ? {
                                                        ...p,
                                                        helpDescription: helpDescriptionTextarea?.value || '',
                                                        lookupSql: lookupSqlTextarea?.value || '',
                                                        validation: validationTextarea?.value || '',
                                                        defaultValueSql: defaultValueTextarea?.value || ''
                                                    }
                                                    : p
                                            );
                                            setState(prev => ({
                                                ...prev,
                                                parametersList: updated,
                                                parameterDetailsEditingId: null,
                                                parameterDetailsDialogOpen: false,
                                                parameterDetailsItem: null,
                                                parameterDetailsLookupSql: '',
                                                parameterDetailsValidation: '',
                                                parameterDetailsHelpDescription: '',
                                                parameterDetailsDefaultValue: ''
                                            }));
                                        }
                                    })
                                    .catch(err => {
                                        alert('Error saving parameter: ' + err.toString());
                                    });
                            }}
                        >Save</button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.permissionsDialogOpen && state.permissionsItem && (
                <Dialog
                    title="Permissions"
                    onClose={closePermissionsDialog}
                    width={1200}
                    height={700}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: 'calc(100% + 32px)', margin: '0 -16px', padding: 0, boxSizing: 'border-box' }}>
                        <div style={{ padding: '0 8px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                Query ID: {state.permissionsItem?.clientQueryID} | Code: {state.permissionsItem?.clientQueryCode}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
                            <TabStrip selected={state.permissionsSelectedTab} onSelect={(e) => setState(prev => ({ ...prev, permissionsSelectedTab: e.selected }))} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', margin: 0, padding: 0 }}>
                                <TabStripTab title="SPL Pages">
                                    <Grid data={permissionsSplPagesGridData} dataItemKey="value" sortable={true} sort={state.permissionsSplPagesSort} onSortChange={handlePermissionsSplPagesSortChange} scrollable='scrollable' style={{ height: '500px' }} onRowClick={handleSplPagesGridRowClick}>
                                        <GridColumn
                                            field="selected"
                                            width={50}
                                            filterable={false}
                                            sortable={true}
                                            cell={(props) => {
                                                return (
                                                <td style={{ 
                                                    textAlign: 'center'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={state.permissionsSplPagesSelected.includes(Number(props.dataItem.value))}
                                                        onChange={e => {
                                                            const value = Number(props.dataItem.value);
                                                            const updatedSelection = e.target.checked
                                                                ? [...state.permissionsSplPagesSelected, value]
                                                                : state.permissionsSplPagesSelected.filter(v => v !== value);
                                                            setState(prev => ({
                                                                ...prev,
                                                                permissionsSplPagesSelected: updatedSelection
                                                            }));
                                                            saveSplPageClientQuery(updatedSelection);
                                                        }}
                                                    />
                                                </td>
                                                );
                                            }}
                                        />
                                        <GridColumn field="multiSelect" title="Multi Select" width={100} sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-center-cell">
                                                <input
                                                    type="checkbox"
                                                    checked={props.dataItem.multiSelect || false}
                                                    onChange={e => {
                                                        handleSplPageMultiSelectChange(props.dataItem, e.target.checked);
                                                    }}
                                                />
                                            </td>
                                            );
                                        }} />
                                        <GridColumn field="value" title="ID" width={100} sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.value}
                                            </td>
                                            );
                                        }} />
                                        <GridColumn field="display" title="Description" sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.display}
                                            </td>
                                            );
                                        }} />
                                    </Grid>
                                </TabStripTab>
                                <TabStripTab title="User Roles">
                                    <Grid data={permissionsUserRolesGridData} dataItemKey="value" sortable={true} sort={state.permissionsUserRolesSort} onSortChange={handlePermissionsUserRolesSortChange} scrollable='scrollable' style={{ height: '500px' }} onRowClick={handleUserRolesGridRowClick}>
                                        <GridColumn
                                            field="selected"
                                            width={50}
                                            filterable={false}
                                            sortable={true}
                                            cell={(props) => {
                                                return (
                                                <td style={{ 
                                                    textAlign: 'center'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={state.permissionsUserRolesSelected.includes(Number(props.dataItem.value))}
                                                        onChange={e => {
                                                            const value = Number(props.dataItem.value);
                                                            const updatedSelection = e.target.checked
                                                                ? [...state.permissionsUserRolesSelected, value]
                                                                : state.permissionsUserRolesSelected.filter(v => v !== value);
                                                            setState(prev => ({
                                                                ...prev,
                                                                permissionsUserRolesSelected: updatedSelection
                                                            }));
                                                            saveUserRoleClientQuery(updatedSelection);
                                                        }}
                                                    />
                                                </td>
                                                );
                                            }}
                                        />
                                        <GridColumn field="value" title="Value" width={100} sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.value}
                                            </td>
                                            );
                                        }} />
                                        <GridColumn field="display" title="Display" sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.display}
                                            </td>
                                            );
                                        }} />
                                    </Grid>
                                </TabStripTab>
                                <TabStripTab title="Packlist Types">
                                    <Grid data={permissionsPacklistTypesGridData} dataItemKey="value" sortable={true} sort={state.permissionsPacklistTypesSort} onSortChange={handlePermissionsPacklistTypesSortChange} scrollable='scrollable' style={{ height: '500px' }} onRowClick={handlePacklistTypesGridRowClick}>
                                        <GridColumn
                                            field="selected"
                                            width={50}
                                            filterable={false}
                                            sortable={true}
                                            cell={(props) => {
                                                return (
                                                <td style={{ 
                                                    textAlign: 'center'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={state.permissionsPacklistTypesSelected.includes(Number(props.dataItem.value))}
                                                        onChange={e => {
                                                            const value = Number(props.dataItem.value);
                                                            const updatedSelection = e.target.checked
                                                                ? [...state.permissionsPacklistTypesSelected, value]
                                                                : state.permissionsPacklistTypesSelected.filter(v => v !== value);
                                                            setState(prev => ({
                                                                ...prev,
                                                                permissionsPacklistTypesSelected: updatedSelection
                                                            }));
                                                            savePacklistTypesClientQuery(updatedSelection);
                                                        }}
                                                    />
                                                </td>
                                                );
                                            }}
                                        />
                                        <GridColumn field="value" title="Value" width={100} sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.value}
                                            </td>
                                            );
                                        }} />
                                        <GridColumn field="display" title="Display" sortable={true} cell={(props) => {
                                            return (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {props.dataItem.display}
                                            </td>
                                            );
                                        }} />
                                    </Grid>
                                </TabStripTab>
                                <TabStripTab title="Item Types">
                                    <div style={{ display: 'flex', width: '100%', height: '500px', gap: 0, overflow: 'hidden' }}>
                                        <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
                                            <div style={{ padding: '8px', fontWeight: 'bold', fontSize: '13px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5', flexShrink: 0 }}>
                                                Allowed Item Types
                                            </div>
                                            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                                        <Grid 
                                                data={permissionsItemTypesGridData} 
                                                dataItemKey="value" 
                                                sortable={true}
                                                scrollable='scrollable'
                                                sort={state.permissionsItemTypesSort}
                                                onSortChange={handlePermissionsItemTypesSortChange}
                                                onRowClick={(e) => {
                                                    setState(prev => ({
                                                        ...prev,
                                                        itemTypesGridSelectedValue: e.dataItem.value,
                                                        itemTypePacklistsSelectedItemTypeValue: e.dataItem.value
                                                    }));
                                                    if (state.permissionsItem) {
                                                    fetchItemTypeClientQueryPacklistTypes(e.dataItem.value, state.permissionsItem.clientQueryID);
                                                    }
                                                }}
                                                >
                                                <GridColumn
                                                    field="selected"
                                                    width={50}
                                                    filterable={false}
                                                    sortable={true}
                                                    cell={(props) => {
                                                        return (
                                                        <td style={{ 
                                                            textAlign: 'center'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={state.permissionsItemTypesSelected.includes(Number(props.dataItem.value))}
                                                                onChange={e => {
                                                                    const value = Number(props.dataItem.value);
                                                                    const updatedSelection = e.target.checked
                                                                        ? [...state.permissionsItemTypesSelected, value]
                                                                        : state.permissionsItemTypesSelected.filter(v => v !== value);
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        permissionsItemTypesSelected: updatedSelection
                                                                    }));
                                                                    saveItemTypesClientQuery(updatedSelection);
                                                                    // if (e.target.checked) {
                                                                    //     fetchItemTypeClientQueryPacklistTypes(value);
                                                                    // } else if (updatedSelection.length > 0) {
                                                                    //     fetchItemTypeClientQueryPacklistTypes(updatedSelection[0]);
                                                                    // }
                                                                }}
                                                            />
                                                        </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn field="value" title="Value" width={100} sortable={true} cell={(props) => {
                                                    return (
                                                    <td className="tmi-dialog-grid-text-cell">
                                                        {props.dataItem.value}
                                                    </td>
                                                    );
                                                }} />
                                                <GridColumn field="display" title="Display" sortable={true} cell={(props) => {
                                                    return (
                                                    <td className="tmi-dialog-grid-text-cell">
                                                        {props.dataItem.display}
                                                    </td>
                                                    );
                                                }} />
                                            </Grid>
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                                            <div style={{ padding: '8px', fontWeight: 'bold', fontSize: '13px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5', flexShrink: 0 }}>
                                                Enabled for Selected Packlist Types (or All if none selected)
                                            </div>
                                            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                                            <Grid 
                                                data={itemTypePacklistsGridData} 
                                                dataItemKey="value" 
                                                sortable={true}
                                                scrollable='scrollable'
                                                sort={state.itemTypePacklistsSort}
                                                onSortChange={handleItemTypePacklistsSortChange}
                                                onRowClick={handleItemTypePacklistsGridRowClick}
                                            >
                                                    <GridColumn
                                                        field="selected"
                                                        width={50}
                                                        filterable={false}
                                                        sortable={true}
                                                        cell={(props) => {
                                                            return (
                                                            <td style={{ 
                                                                textAlign: 'center'
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={state.itemTypePacklistsSelected.includes(Number(props.dataItem.value))}
                                                                    onChange={e => {
                                                                        const value = Number(props.dataItem.value);
                                                                        const updatedSelection = e.target.checked
                                                                            ? [...state.itemTypePacklistsSelected, value]
                                                                            : state.itemTypePacklistsSelected.filter(v => v !== value);
                                                                        setState(prev => ({
                                                                            ...prev,
                                                                            itemTypePacklistsSelected: updatedSelection
                                                                        }));
                                                                        saveItemTypeClientQueryPackListType(updatedSelection);
                                                                    }}
                                                                />
                                                            </td>
                                                            );
                                                        }}
                                                    />
                                                    <GridColumn field="value" title="Value" width={100} sortable={true} cell={(props) => {
                                                        return (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {props.dataItem.value}
                                                        </td>
                                                        );
                                                    }} />
                                                    <GridColumn field="display" title="Display" sortable={true} cell={(props) => {
                                                        return (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {props.dataItem.display}
                                                        </td>
                                                        );
                                                    }} />
                                                </Grid>
                                            </div>
                                        </div>
                                    </div>
                                </TabStripTab>
                            </TabStrip>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePermissionsDialog}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.copyToNewDlgOpen && state.copyToNewSourceItem && (
                <Dialog
                    title="Copy to New Client Query"
                    onClose={closeCopyToNewDialog}
                    width={500}
                >
                    <div style={{ padding: '16px 0' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Description:</label>
                            <input
                                type="text"
                                value={state.copyToNewDescription}
                                onChange={e => setState(prev => ({ ...prev, copyToNewDescription: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter new description"
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Code:</label>
                            <input
                                type="text"
                                value={state.copyToNewCode}
                                onChange={e => setState(prev => ({ ...prev, copyToNewCode: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter new code"
                            />
                        </div>
                        <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '16px' }}>
                            <p style={{ margin: 0, fontSize: '14px' }}>
                                <strong>This will copy:</strong>
                                <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
                                    <li>Scripts (SQL, Validation, Pre-Validation)</li>
                                    <li>Parameters</li>
                                    <li>Permissions (SPL Pages, Packlist Types, Item Types)</li>
                                </ul>
                            </p>
                        </div>
                        {state.copyToNewError && (
                            <div style={{ color: 'red', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                                {state.copyToNewError}
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeCopyToNewDialog} disabled={state.copyToNewSaving}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitCopyToNew}
                            disabled={state.copyToNewSaving || !state.copyToNewDescription.trim() || !state.copyToNewCode.trim()}
                        >
                            {state.copyToNewSaving ? 'Creating...' : 'Create Copy'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <ProjectSelectionDialog
                open={state.importFromProjectDlgOpen}
                title="Select Project to Import From"
                apiUrl={props.userInf.currProject.apiUrl}
                token={props.userInf.token}
                emptyMessage="No projects available to import from"
                onClose={closeImportFromProjectDialog}
                onSelectProject={(project) => handleImportProjectSelection(project.value)}
            />

            {state.importQuerySelectDlgOpen && state.importSelectedProject && (
                <Dialog
                    title={`Select Query from ${state.importSelectedProject.display}`}
                    onClose={closeImportQuerySelectDialog}
                    width={900}
                    height={700}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 50px)', width: 'calc(100% + 32px)', margin: '0 -16px', padding: 0, boxSizing: 'border-box' }}>
                        {state.importQueryError && (
                            <div style={{ color: 'red', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', margin: 0 }}>
                                {state.importQueryError}
                            </div>
                        )}

                        {state.importQueryLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <span className="k-icon k-font-icon k-i-loading" style={{ fontSize: '32px' }} />
                                <p style={{ margin: '8px 0 0 0' }}>Loading queries...</p>
                            </div>
                        ) : (
                            <>
                                {state.importAvailableQueries.length === 0 ? (
                                    <p style={{ color: '#888', margin: 0 }}>No queries available in this project</p>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px', padding: '8px 8px 0 8px' }}>
                                        <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Select a query:</label>
                                        <select
                                            value={state.importSelectedQuery?.value || ''}
                                            onChange={e => {
                                                const query = state.importAvailableQueries.find(q => q.value === e.target.value);
                                                const selectedProjectValue = state.importSelectedProject?.value || '';
                                                setState(prev => ({
                                                    ...prev,
                                                    importSelectedQuery: query || null,
                                                    importDetailsError: '',
                                                    importScriptSQL: '',
                                                    importScriptValidation: '',
                                                    importScriptPreValidation: '',
                                                    importScriptTabIndex: 0,
                                                }));

                                                if (query && selectedProjectValue) {
                                                    fetchImportQueryDetails(selectedProjectValue, query.value);
                                                }
                                            }}
                                            className="k-input k-input-md k-rounded-md"
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">-- Select a query --</option>
                                            {state.importAvailableQueries.map(q => (
                                                <option key={q.value} value={q.value}>
                                                    {q.display}
                                                </option>
                                            ))}
                                        </select>
                                        {state.importDetailsLoading && (
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                                                Loading selected query scripts...
                                            </div>
                                        )}
                                        {state.importDetailsError && (
                                            <div style={{ color: 'red', marginTop: '6px', fontSize: '12px' }}>
                                                {state.importDetailsError}
                                            </div>
                                        )}
                                        </div>

                                        {state.importSelectedQuery && (
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
                                                <TabStrip selected={state.importScriptTabIndex} onSelect={(e) => setState(prev => ({ ...prev, importScriptTabIndex: e.selected }))} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', margin: 0, padding: 0 }}>
                                                    <TabStripTab title="SQL Script">
                                                        <textarea
                                                            className="k-input k-input-md k-rounded-md"
                                                            value={state.importScriptSQL}
                                                            onChange={e => setState(prev => ({ ...prev, importScriptSQL: e.target.value }))}
                                                            style={{
                                                                width: '850px',
                                                                height: '430px',
                                                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                                fontSize: '12px',
                                                                padding: '8px',
                                                                overflowY: 'auto',
                                                                border: 'none',
                                                                borderRadius: '0px',
                                                                resize: 'none',
                                                                boxSizing: 'border-box',
                                                                margin: 0,
                                                                display: 'block',
                                                            }}
                                                        />
                                                    </TabStripTab>
                                                    <TabStripTab title="Validation Script">
                                                        <textarea
                                                            className="k-input k-input-md k-rounded-md"
                                                            value={state.importScriptValidation}
                                                            onChange={e => setState(prev => ({ ...prev, importScriptValidation: e.target.value }))}
                                                            style={{
                                                                width: '850px',
                                                                height: '430px',
                                                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                                fontSize: '12px',
                                                                padding: '8px',
                                                                overflowY: 'auto',
                                                                border: 'none',
                                                                borderRadius: '0px',
                                                                resize: 'none',
                                                                boxSizing: 'border-box',
                                                                margin: 0,
                                                                display: 'block',
                                                            }}
                                                        />
                                                    </TabStripTab>
                                                    <TabStripTab title="Pre-Validation Script">
                                                        <textarea
                                                            className="k-input k-input-md k-rounded-md"
                                                            value={state.importScriptPreValidation}
                                                            onChange={e => setState(prev => ({ ...prev, importScriptPreValidation: e.target.value }))}
                                                            style={{
                                                                width: '850px',
                                                                height: '430px',
                                                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                                fontSize: '12px',
                                                                padding: '8px',
                                                                overflowY: 'auto',
                                                                border: 'none',
                                                                borderRadius: '0px',
                                                                resize: 'none',
                                                                boxSizing: 'border-box',
                                                                margin: 0,
                                                                display: 'block',
                                                            }}
                                                        />
                                                    </TabStripTab>
                                                </TabStrip>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeImportQuerySelectDialog} disabled={state.importQueryLoading}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={handleImportQuerySelected}
                            disabled={!state.importSelectedQuery || state.importQueryLoading}
                        >
                            Next
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.importImportDescription && state.importSelectedProject && state.importSelectedQuery && (
                <Dialog
                    title="Import Query Settings"
                    onClose={() => {
                        setState(prev => ({
                            ...prev,
                            importImportDescription: ''
                        }));
                    }}
                    width={900}
                    height={700}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 50px)', width: 'calc(100% + 32px)', margin: '0 -16px', padding: 0, boxSizing: 'border-box' }}>
                        <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                            <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>Source Details:</p>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Project:</strong> {state.importSelectedProject.display}
                            </p>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Query:</strong> {state.importSelectedQuery.display}
                            </p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px', marginBottom: '12px' }}>
                            <label style={{ fontWeight: 'bold' }}>New Description</label>
                            <input
                                type="text"
                                value={state.importImportDescription}
                                onChange={e => setState(prev => ({ ...prev, importImportDescription: e.target.value }))}
                                className="k-input k-input-md k-rounded-md"
                                style={{ width: '100%', boxSizing: 'border-box' }}
                                maxLength={200}
                            />
                        </div>

                        {state.importDetailsError && (
                            <div style={{ color: 'red', padding: '8px', marginBottom: '8px' }}>{state.importDetailsError}</div>
                        )}

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
                            <TabStrip selected={state.importScriptTabIndex} onSelect={(e) => setState(prev => ({ ...prev, importScriptTabIndex: e.selected }))} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', margin: 0, padding: 0 }}>

                                <TabStripTab title="SQL Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        value={state.importScriptSQL}
                                        onChange={e => setState(prev => ({ ...prev, importScriptSQL: e.target.value }))}
                                        style={{
                                            width: '850px',
                                            height: '340px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>
                                <TabStripTab title="Validation Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        value={state.importScriptValidation}
                                        onChange={e => setState(prev => ({ ...prev, importScriptValidation: e.target.value }))}
                                        style={{
                                            width: '850px',
                                            height: '340px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>
                                <TabStripTab title="Pre-Validation Script">
                                    <textarea
                                        className="k-input k-input-md k-rounded-md"
                                        value={state.importScriptPreValidation}
                                        onChange={e => setState(prev => ({ ...prev, importScriptPreValidation: e.target.value }))}
                                        style={{
                                            width: '850px',
                                            height: '340px',
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '12px',
                                            padding: '8px',
                                            overflowY: 'auto',
                                            border: 'none',
                                            borderRadius: '0px',
                                            resize: 'none',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            display: 'block',
                                        }}
                                    />
                                </TabStripTab>

                            </TabStrip>
                        </div>

                        <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginTop: '12px' }}>
                            <p style={{ margin: 0, fontSize: '13px' }}>
                                <strong>This will import:</strong>
                                <ul style={{ margin: '8px 0 0 18px', paddingLeft: 0, fontSize: '13px' }}>
                                    <li>Scripts (SQL, Validation, Pre-Validation)</li>
                                    <li>Parameters</li>
                                </ul>
                            </p>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={() => {
                            setState(prev => ({
                                ...prev,
                                importImportDescription: ''
                            }));
                        }} disabled={state.importImporting}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitImportFromProject}
                            disabled={state.importImporting || !state.importImportDescription.trim()}
                        >
                            {state.importImporting ? 'Importing...' : 'Import Query'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

export default connector(ClientQueries);
